import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  CreditCard, 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  FileText,
  Euro,
  Clock,
  RefreshCcw,
  ExternalLink
} from 'lucide-react';
import { formatCurrency } from '@/lib/translations';

const RESERVATION_FEE = 1000.00;
const STRIPE_PAYMENT_LINK = process.env.REACT_APP_STRIPE_PAYMENT_LINK;

export const PaymentReservationSection = ({ 
  formData, 
  onPaymentSuccess, 
  t, 
  language 
}) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  const reservation = t.reservation;

  const handlePayment = () => {
    if (!termsAccepted) return;
    
    // Validate payment link is configured
    if (!STRIPE_PAYMENT_LINK) {
      console.error('REACT_APP_STRIPE_PAYMENT_LINK environment variable is not configured');
      return;
    }
    
    setIsRedirecting(true);
    
    // Build the Stripe Payment Link URL with prefilled customer info
    const params = new URLSearchParams({
      prefilled_email: formData.customerInfo.email || '',
      client_reference_id: `${formData.customerInfo.name}_${Date.now()}`
    });
    
    const paymentUrl = `${STRIPE_PAYMENT_LINK}?${params.toString()}`;
    
    // Redirect to Stripe Payment Link
    window.location.href = paymentUrl;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <CreditCard className="w-5 h-5 text-primary" />
            {reservation.title}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">{reservation.description}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Customer Number Display */}
          {formData.customerInfo.customerNumber && (
            <div className="p-4 rounded-lg bg-success/10 border border-success/20 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'de' ? 'Ihre Kundennummer' : 'Your Customer Number'}
                </p>
                <p className="text-xl font-bold font-mono text-success">
                  {formData.customerInfo.customerNumber}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-success" />
            </div>
          )}

          {/* Reservation Fee Amount */}
          <div className="p-6 rounded-lg bg-primary/5 border border-primary/20 text-center">
            <p className="text-sm text-muted-foreground mb-2">{reservation.feeLabel}</p>
            <p className="text-4xl font-bold text-primary">
              {formatCurrency(RESERVATION_FEE, locale)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{reservation.vatNote}</p>
          </div>

          {/* Terms and Conditions */}
          <div className="rounded-lg border border-border">
            <div className="p-4 bg-muted/30 border-b border-border">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                <h4 className="font-semibold text-foreground">{reservation.termsTitle}</h4>
              </div>
            </div>
            <ScrollArea className="h-64 p-4">
              <div className="space-y-4 text-xs text-muted-foreground leading-relaxed">
                <div>
                  <h5 className="font-semibold text-foreground mb-2">(1) {reservation.terms.section1Title}</h5>
                  <p>{reservation.terms.section1Content}</p>
                </div>
                
                <Separator />
                
                <div>
                  <h5 className="font-semibold text-foreground mb-2">(2) {reservation.terms.section2Title}</h5>
                  <p className="mb-2">{reservation.terms.section2Content}</p>
                  <ul className="list-disc pl-4 space-y-1">
                    {reservation.terms.section2List.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                  <p className="mt-2">{reservation.terms.section2Note}</p>
                </div>
                
                <Separator />
                
                <div>
                  <h5 className="font-semibold text-foreground mb-2">(3) {reservation.terms.section3Title}</h5>
                  <p>{reservation.terms.section3Content}</p>
                </div>
                
                <Separator />
                
                <div>
                  <h5 className="font-semibold text-foreground mb-2">(4) {reservation.terms.section4Title}</h5>
                  <p>{reservation.terms.section4Content}</p>
                </div>
                
                <Separator />
                
                <div>
                  <h5 className="font-semibold text-foreground mb-2">(5) {reservation.terms.section5Title}</h5>
                  <p>{reservation.terms.section5Content}</p>
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* Terms Acceptance */}
          <div className={`p-4 rounded-lg border-2 transition-colors ${
            termsAccepted 
              ? 'bg-success/5 border-success/30' 
              : 'bg-card border-border'
          }`}>
            <div className="flex items-start gap-3">
              <Checkbox
                id="reservationTerms"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                className="mt-0.5"
              />
              <Label 
                htmlFor="reservationTerms" 
                className="text-sm cursor-pointer leading-relaxed"
              >
                {reservation.acceptTerms}
              </Label>
            </div>
          </div>

          {/* Payment Button */}
          <Button
            onClick={handlePayment}
            disabled={!termsAccepted || isRedirecting}
            className="w-full"
            variant="premium"
            size="lg"
          >
            {isRedirecting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {reservation.processing}
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                {reservation.payButton} - {formatCurrency(RESERVATION_FEE, locale)}
                <ExternalLink className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>

          {/* Info note */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
            <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
            <p className="text-xs text-muted-foreground">
              {reservation.infoNote}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentReservationSection;
